// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import CollectionsPage from './pages/CollectionsPage';
import CollectionDetailPage from './pages/CollectionDetailPage';
import NftDetailsPage from './pages/NftDetailsPage';
import WalletPage from './pages/WalletPage';
import WalletPageTokens from './pages/WalletPageTokens';
import WalletHistoryPage from './pages/WalletHistoryPage';
import ScrollToTop from './components/ScrollToTop';
import { WalletProvider } from './components/WalletContext';
import WalletSalesPage from './pages/WalletSalesPage';
import WalletListingsPage from './pages/WalletListingsPage';
import WalletPurchasesPage from './pages/WalletPurchasesPage';
import AssetDetailsPage from './pages/AssetDetailsPage';
import TokenDetailsPage from './pages/TokenDetailsPage';
import RewardsPage from './pages/RewardsPage';
import CreatorPage from './pages/CreatorPage';
import AdminPage from './pages/AdminPage';
import SubmitMaterialsPage from './pages/SubmitMaterialsPage';

import DropsPage from './pages/DropsPage';
import StatsPage from './pages/StatsPage';

import ChatComponent from './components/ChatComponent';
import MintPage from './pages/MintPage';
import './App.css';
import { ThemeProvider } from './components/ThemeContext'; // Ensure this import exists
import { library } from '@fortawesome/fontawesome-svg-core';
import { faMoon, faSun, faCoins, faCodeBranch, faWallet } from '@fortawesome/free-solid-svg-icons';
import { faTwitter, faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
library.add(faMoon, faSun, faCoins, faCodeBranch, faWallet, faTwitter, faDiscord, faTelegram);

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 2 * 60 * 1000, // 2 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ScrollToTop />
        <WalletProvider>
          <ThemeProvider>
            <div className="App">
              <Header />
              <Routes>
                <Route path="/" element={<LayoutWithFooter />}>
                  <Route index element={<HomePage />} />
                  <Route path="collections" element={<CollectionsPage />} />
                  <Route path="collections/:collectionId" element={<CollectionDetailPage />} />
                  <Route path="collections/:collectionId/:nftId" element={<NftDetailsPage />} />
                  <Route path="wallet/tokens/:assetId" element={<TokenDetailsPage />} />
                  <Route path="rewards" element={<RewardsPage />} />
                  <Route path="drops" element={<DropsPage />} />
                  <Route path="mint" element={<MintPage />} />
                  <Route path="stats" element={<StatsPage />} />
                  <Route path="creator" element={<CreatorPage />} />
                  <Route path="admin" element={<AdminPage />} />
                  <Route path="submit-materials" element={<SubmitMaterialsPage />} />
                  <Route path="wallet/:assetId" element={<AssetDetailsPage />} />
                  <Route path="wallet" element={<Outlet />}>
                    <Route index element={<WalletPage />} />
                    <Route path="sfts" element={<WalletPage />} />
                    <Route path="tokens" element={<WalletPageTokens />} />
                    <Route path="history" element={<WalletHistoryPage />}>
                      <Route index element={<WalletHistoryPage />} />
                      <Route path="sales" element={<WalletSalesPage />} />
                      <Route path="listings" element={<WalletListingsPage />} />
                      <Route path="purchases" element={<WalletPurchasesPage />} />
                    </Route>
                  </Route>
                </Route>
              </Routes>
              <ChatComponent />
            </div>
          </ThemeProvider>
        </WalletProvider>
      </Router>
    </QueryClientProvider>
  );
}

function LayoutWithFooter() {
  return (
    <>
      <Outlet />
      <Footer />
    </>
  );
}

export default App;